import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsuarioDataService } from '../../shared/service/usuario-data.services';
import { TooltipService, ToolTipsAvailable } from '../../../../../shared/services/tooltip.service';

export interface FechamentoExecutivoItem {
  UsuarioId: string;
  Executivo: string;
  DataUltimoFechamento: any;
  diasDesdeUltimoFechamento: number;
  GerenteExecutivoId: string | null;
  GerenteExecutivo: string | null;
}

/** Um gerente e os executivos do time dele (subordinados com o mesmo GerenteExecutivoId). */
export interface TermometroBlocoGerente {
  gerenteId: string;
  nomeGerente: string;
  gerente: FechamentoExecutivoItem | null;
  executivosDoTime: FechamentoExecutivoItem[];
}

@Component({
  selector: 'ngx-termometro-data-fechamento',
  templateUrl: './termometro-data-fechamento.component.html',
  styleUrls: ['./termometro-data-fechamento.component.scss'],
})
export class TermometroDataFechamentoComponent implements OnInit, OnDestroy, OnChanges {
  @Input() usuarioId: string;
  @Input() ehExecutivo = false;

  @Output() fechouDicaTermometro = new EventEmitter<void>();

  ultimoFechamentoLoading = false;
  ultimoFechamentoSemDados = false;
  ultimoFechamentoDataFormatada: string | null = null;
  ultimoFechamentoDias: number | null = null;
  listaFechamentosExecutivos: FechamentoExecutivoItem[] = [];
  blocosPorGerente: TermometroBlocoGerente[] = [];
  outrosExecutivos: FechamentoExecutivoItem[] = [];
  meuFechamentoNoIndicador: FechamentoExecutivoItem | null = null;

  mostrarTooltipTermometro = false;
  private dicaTermometroFoiExibidaNestaSessao = false;
  private termometroTooltipService: TooltipService;
  private destroy$ = new Subject<void>();
  private carregarIndicadorSub: Subscription | null = null;
  private boundDocClick = (event: MouseEvent) => this.onDocumentClick(event);

  constructor(
    private usuarioDataService: UsuarioDataService,
    private cdr: ChangeDetectorRef,
  ) {
    this.termometroTooltipService = new TooltipService(ToolTipsAvailable.HeaderTermometroTooltip);
  }

  get estaDicaTermometroAberta(): boolean {
    return this.mostrarTooltipTermometro;
  }

  ngOnInit(): void {
    document.addEventListener('click', this.boundDocClick);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.usuarioId) {
      return;
    }
    const uid = changes['usuarioId'];
    const ex = changes['ehExecutivo'];
    const mudouUsuario =
      !!uid && (uid.isFirstChange() || uid.previousValue !== uid.currentValue);
    const mudouExecutivo =
      !!ex && (ex.isFirstChange() || ex.previousValue !== ex.currentValue);
    if (mudouUsuario || mudouExecutivo) {
      this.carregarIndicador();
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.boundDocClick);
    this.carregarIndicadorSub?.unsubscribe();
    this.carregarIndicadorSub = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      this.mostrarTooltipTermometro &&
      !target.closest('.header-edu-tooltip--termometro') &&
      !target.closest('.fechamento-edu-anchor')
    ) {
      this.mostrarTooltipTermometro = false;
      this.dicaTermometroFoiExibidaNestaSessao = true;
      this.fechouDicaTermometro.emit();
      this.cdr.detectChanges();
    }
  }

  agendarDicaSeNecessario(): void {
    if (!this.termometroTooltipService.shouldShowTooltip() || this.dicaTermometroFoiExibidaNestaSessao) {
      return;
    }
    if (this.mostrarTooltipTermometro) {
      return;
    }
    this.mostrarTooltipTermometro = true;
    this.cdr.markForCheck();
  }

  fecharTooltipTermometro(dontShowAgain = false): void {
    this.mostrarTooltipTermometro = false;
    this.dicaTermometroFoiExibidaNestaSessao = true;
    this.termometroTooltipService.dismissTooltip(dontShowAgain);
    this.fechouDicaTermometro.emit();
    this.cdr.markForCheck();
  }

  carregarIndicador(): void {
    if (!this.usuarioId) {
      return;
    }

    this.carregarIndicadorSub?.unsubscribe();
    this.carregarIndicadorSub = null;

    this.ultimoFechamentoLoading = true;
    this.ultimoFechamentoSemDados = false;
    this.ultimoFechamentoDataFormatada = null;
    this.ultimoFechamentoDias = null;
    this.listaFechamentosExecutivos = [];
    this.blocosPorGerente = [];
    this.outrosExecutivos = [];
    this.meuFechamentoNoIndicador = null;

    this.carregarIndicadorSub = this.usuarioDataService
      .CarregaDataUltimoFechamentoExecutivo()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.ultimoFechamentoLoading = false;

          const listaBruta = Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response?.result)
                ? response.result
                : response
                  ? [response]
                  : [];

          if (!listaBruta?.length) {
            this.ultimoFechamentoSemDados = true;
            this.cdr.detectChanges();
            return;
          }

          const itensValidos = listaBruta
            .map((item: any) => ({
              UsuarioId: item?.UsuarioId,
              Executivo: item?.Executivo ?? item?.executivo ?? '',
              DataUltimoFechamento: item?.DataUltimoFechamento,
              GerenteExecutivoId: this.normalizarGerenteExecutivoId(
                item?.GerenteExecutivoId ?? item?.gerenteExecutivoId,
              ),
              GerenteExecutivo: this.normalizarGerenteExecutivo(
                item?.GerenteExecutivo ?? item?.gerenteExecutivo,
              ),
            }))
            .filter(
              (item: any) =>
                !!item.UsuarioId && item.Executivo && this.parseDataDotNet(item.DataUltimoFechamento) !== null,
            );

          if (!itensValidos?.length) {
            this.ultimoFechamentoSemDados = true;
            this.cdr.detectChanges();
            return;
          }

          if (this.ehExecutivo) {
            const usuarioIdAtual = String(this.usuarioId);
            const meusDados = itensValidos.filter(x => String(x.UsuarioId) === usuarioIdAtual);

            if (!meusDados?.length) {
              this.ultimoFechamentoSemDados = true;
              this.cdr.detectChanges();
              return;
            }

            const ultimo = meusDados.reduce((prev, curr) => {
              const dPrev = this.parseDataDotNet(prev.DataUltimoFechamento);
              const dCurr = this.parseDataDotNet(curr.DataUltimoFechamento);
              if (!dPrev || !dCurr) {
                return prev;
              }
              return dCurr.getTime() > dPrev.getTime() ? curr : prev;
            });

            const data = this.parseDataDotNet(ultimo.DataUltimoFechamento);
            if (!data) {
              this.ultimoFechamentoSemDados = true;
              this.cdr.detectChanges();
              return;
            }

            const dias = this.calcularDiasDesde(data);
            this.ultimoFechamentoDias = dias;
            this.ultimoFechamentoDataFormatada = this.formatarDataBR(data);
          } else {
            const mapaPorUsuario = new Map<string, (typeof itensValidos)[number]>();
            for (const item of itensValidos) {
              const key = String(item.UsuarioId);
              const dataItem = this.parseDataDotNet(item.DataUltimoFechamento);
              if (!dataItem) {
                continue;
              }

              const atual = mapaPorUsuario.get(key);
              if (!atual) {
                mapaPorUsuario.set(key, item);
                continue;
              }

              const dataAtual = this.parseDataDotNet(atual.DataUltimoFechamento);
              if (!dataAtual || dataItem.getTime() > dataAtual.getTime()) {
                mapaPorUsuario.set(key, item);
              }
            }

            const listaCompleta: FechamentoExecutivoItem[] = Array.from(mapaPorUsuario.values())
              .map(item => {
                const data = this.parseDataDotNet(item.DataUltimoFechamento);
                return {
                  UsuarioId: String(item.UsuarioId),
                  Executivo: item.Executivo,
                  DataUltimoFechamento: item.DataUltimoFechamento,
                  diasDesdeUltimoFechamento: data ? this.calcularDiasDesde(data) : 0,
                  GerenteExecutivoId: item.GerenteExecutivoId,
                  GerenteExecutivo: item.GerenteExecutivo,
                };
              })
              .sort((a, b) => this.compararPorDataMaisAntigaPrimeiro(a, b));

            const usuarioIdAtual = String(this.usuarioId);
            const meuNaLista = listaCompleta.find(x => String(x.UsuarioId) === usuarioIdAtual) ?? null;
            this.meuFechamentoNoIndicador = meuNaLista;

            // Para perfis não executivos, exibir a base completa no indicador.
            this.listaFechamentosExecutivos = listaCompleta;

            if (meuNaLista) {
              const dataMinha = this.parseDataDotNet(meuNaLista.DataUltimoFechamento);
              if (dataMinha) {
                const diasMinha = this.calcularDiasDesde(dataMinha);
                this.ultimoFechamentoDataFormatada = this.formatarDataBR(dataMinha);
                this.ultimoFechamentoDias = diasMinha;
              }
            } else {
              this.ultimoFechamentoDataFormatada = null;
              this.ultimoFechamentoDias = null;
            }

            const montado = this.montarEstruturaPorGerente(this.listaFechamentosExecutivos);
            this.blocosPorGerente = montado.blocos;
            this.outrosExecutivos = montado.outros;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.ultimoFechamentoLoading = false;
          this.ultimoFechamentoSemDados = true;
          this.cdr.detectChanges();
        },
      });
  }

  parseDataUltimoFechamentoParaTexto(value: any): string {
    const data = this.parseDataDotNet(value);
    return data ? this.formatarDataBR(data) : '—';
  }

  private normalizarGerenteExecutivo(valor: any): string | null {
    if (valor === null || valor === undefined) {
      return null;
    }
    const s = String(valor).trim();
    return s.length > 0 ? s : null;
  }

  private normalizarGerenteExecutivoId(valor: any): string | null {
    if (valor === null || valor === undefined) {
      return null;
    }
    const s = String(valor).trim();
    return s.length > 0 ? s : null;
  }

  /**
   * Por cada gerente referenciado em GerenteExecutivoId: bloco com linha do gerente (data/dias)
   * e lista dos executivos do time. Sem gerente executivo: aba Outros (exceto quem já aparece só como gerente).
   */
  private montarEstruturaPorGerente(lista: FechamentoExecutivoItem[]): {
    blocos: TermometroBlocoGerente[];
    outros: FechamentoExecutivoItem[];
  } {
    const idsGerentesReferenciados = new Set<string>(
      lista
        .map(item => item.GerenteExecutivoId)
        .filter((id): id is string => !!id)
        .map(id => String(id)),
    );

    const gerenteIds = Array.from(
      new Set(
        lista.filter(item => !!item.GerenteExecutivoId).map(item => String(item.GerenteExecutivoId)),
      ),
    );

    const blocos: TermometroBlocoGerente[] = [];

    for (const gerenteId of gerenteIds) {
      const gerente =
        lista.find(item => String(item.UsuarioId) === gerenteId) ?? null;

      const executivosDoTime = lista
        .filter(
          item =>
            String(item.GerenteExecutivoId) === gerenteId &&
            String(item.UsuarioId) !== gerenteId,
        )
        .sort((a, b) => this.compararPorDataMaisAntigaPrimeiro(a, b));

      const nomeGerente =
        gerente?.Executivo ??
        executivosDoTime[0]?.GerenteExecutivo ??
        'Gerente executivo';

      blocos.push({
        gerenteId,
        nomeGerente,
        gerente,
        executivosDoTime,
      });
    }

    blocos.sort((a, b) => {
      const ta = this.dataRepresentativaBloco(a);
      const tb = this.dataRepresentativaBloco(b);
      if (ta !== tb) {
        return ta - tb;
      }
      return (a.nomeGerente || '').localeCompare(b.nomeGerente || '', 'pt-BR');
    });

    const outros = lista
      .filter(
        item =>
          !item.GerenteExecutivoId &&
          !idsGerentesReferenciados.has(String(item.UsuarioId)),
      )
      .sort((a, b) => this.compararPorDataMaisAntigaPrimeiro(a, b));

    return { blocos, outros };
  }

  private dataRepresentativaBloco(bloco: TermometroBlocoGerente): number {
    const tempos: number[] = [];
    if (bloco.gerente) {
      const d = this.parseDataDotNet(bloco.gerente.DataUltimoFechamento);
      if (d) {
        tempos.push(d.getTime());
      }
    }
    for (const e of bloco.executivosDoTime) {
      const d = this.parseDataDotNet(e.DataUltimoFechamento);
      if (d) {
        tempos.push(d.getTime());
      }
    }
    return tempos.length ? Math.min(...tempos) : Number.MAX_SAFE_INTEGER;
  }

  private compararPorDataMaisAntigaPrimeiro(a: FechamentoExecutivoItem, b: FechamentoExecutivoItem): number {
    const dataA = this.parseDataDotNet(a.DataUltimoFechamento);
    const dataB = this.parseDataDotNet(b.DataUltimoFechamento);
    const tempoA = dataA?.getTime() ?? 0;
    const tempoB = dataB?.getTime() ?? 0;
    if (tempoA !== tempoB) {
      return tempoA - tempoB;
    }
    return (a.Executivo || '').localeCompare(b.Executivo || '', 'pt-BR');
  }

  private parseDataDotNet(value: any): Date | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string') {
      const match = /\/Date\((\d+)\)\//.exec(value);
      if (match?.[1]) {
        const ms = Number(match[1]);
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === 'object') {
      const maybe = (value as any).value ?? (value as any).date ?? (value as any).data;
      if (maybe !== undefined) {
        return this.parseDataDotNet(maybe);
      }
    }

    return null;
  }

  private calcularDiasDesde(data: Date): number {
    const d = new Date(data);
    d.setHours(0, 0, 0, 0);

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const diffMs = hoje.getTime() - d.getTime();
    const dias = Math.floor(diffMs / 86400000);
    return dias < 0 ? 0 : dias;
  }

  private formatarDataBR(data: Date): string {
    if (!data) {
      return '';
    }
    return data.toLocaleDateString('pt-BR');
  }
}
